import {
  attr,
  formGroupTemplate,
  html,
  PdfFieldMapping,
  printExpression,
  render
} from '@cumu/shared';
import { target } from '@github/catalyst';
import type { FormBehaviorElement } from '../../forms/behavior-element';
import { controller } from '../controller';
import { dispatchChange, scrollIntoViewIfNeeded } from '../util';
import { PDFViewerBase } from './pdf-viewer-base';
import { AcroFormField, FieldMap, PDFViewerApplication } from './pdfjs';

@controller('pdf-template')
export class PdfTemplateElement extends PDFViewerBase {
  @target declare form: HTMLFormElement;
  @target declare titleInput: HTMLInputElement;
  @target declare startPageInput: HTMLInputElement;
  @target declare endPageInput: HTMLInputElement;
  @target declare noFieldsFlash: HTMLElement;
  @target declare fieldsScript: HTMLScriptElement;
  acroForm: FieldMap = {};
  fieldsById: Record<string, AcroFormField> = {};
  fieldOutlineTimeout = 0;
  removeFieldOutline = () => {};
  viewerOutlineTimeout = 0;
  removeViewerOutline = () => {};
  onAnnotationEditorUiManager = undefined;

  connectedCallback() {
    super.connectedCallback();
    // Fix nested scroll issues.
    addEventListener('click', () => fixScrollTop('click event'));
    addEventListener('focus', () => fixScrollTop('focus event'), {
      capture: true
    });
  }

  async onPdfLoaded(viewerApplication: PDFViewerApplication) {
    // If new, set title and end page.
    if (this.form.querySelector('form-behavior[new]')) {
      const { info } = await viewerApplication.pdfDocument.getMetadata();
      this.titleInput.value = this.titleInput.value || info.Title || '';
      this.endPageInput.value =
        viewerApplication.pdfDocument.numPages.toString();
      (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
    }

    // Set max page numbers.
    this.startPageInput.max = viewerApplication.pdfDocument.numPages.toString();
    this.endPageInput.max = viewerApplication.pdfDocument.numPages.toString();

    // Get form.
    const acroForm = await viewerApplication.pdfDocument.getFieldObjects();

    // If there are no fields, let them know, and return.
    if (
      !acroForm ||
      !Object.values(acroForm).some(fields =>
        fields.some(
          field =>
            ['text', 'checkbox', 'radiobutton'].includes(field.type) &&
            field.editable !== false &&
            field.hidden !== true
        )
      )
    ) {
      this.noFieldsFlash.hidden = false;
      this.form.insertAdjacentHTML(
        'beforeend',
        render(
          html`<input type="hidden" name="mapping/fields:json" value="[]" />`
        )
      );
      (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
      return;
    }

    this.acroForm = acroForm;

    this.fieldsById = Object.values(acroForm)
      .flat()
      .reduce(
        (acc, field) => {
          acc[field.id] = field;
          return acc;
        },
        {} as Record<string, AcroFormField>
      );

    this.iframe.contentWindow?.document.head.insertAdjacentHTML(
      'beforeend',
      render(
        html`<style>
          @keyframes activeFieldOutlineFade {
            0% {
              outline: 4px solid #0969da;
            }
            99.9% {
              outline: 4px solid transparent;
            }
            100% {
              outline: unset;
            }
          }

          .active-field-outline {
            outline: 4px solid #0969da;
          }
          .active-field-outline--fade {
            animation: activeFieldOutlineFade 2s ease-out forwards;
          }
          .cumulus-mapped {
            --annotation-unfocused-field-background: url("data:image/svg+xml;charset=UTF-8,<svg width='1px' height='1px' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' style='fill:rgba(46, 160, 67, 0.15);'/></svg>");
          }
        </style>`
      )
    );

    this.renderMappingFields(viewerApplication);
  }

  onCtrlS(_viewerApplication: PDFViewerApplication): void {
    this.form.requestSubmit();
  }

  onAnnotationFieldClick(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void {
    this.handleAnnotationFieldClickOrFocus(viewerApplication, target, event);
  }

  onAnnotationFieldFocus(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void {
    this.handleAnnotationFieldClickOrFocus(viewerApplication, target, event);
  }

  async handleAnnotationFieldClickOrFocus(
    _viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    _event: Event
  ) {
    const id = `acro-field-${target.dataset.elementId}`;
    const element = document.getElementById(id)!;
    clearTimeout(this.fieldOutlineTimeout);
    this.removeFieldOutline();
    await new Promise<void>(resolve => setTimeout(resolve, 0));
    element.classList.add('element-changed-animation');
    this.removeFieldOutline = () =>
      element.classList.remove('element-changed-animation');
    this.fieldOutlineTimeout = setTimeout(this.removeFieldOutline, 2050);
    scrollIntoViewIfNeeded(element);
    fixScrollTop('handleAnnotationClick - scrollIntoView');
  }

  onAnnotationFieldChange(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    _event: Event
  ): void {
    // sync all fields with the same name.
    const eventFieldId = target.getAttribute('data-element-id')!;
    const name = this.fieldsById[eventFieldId].name;
    const fields = this.acroForm[name].filter(field =>
      ['text', 'checkbox', 'radiobutton'].includes(field.type)
    );
    for (const field of fields) {
      const initialValue = field.value;
      const override = viewerApplication.pdfDocument.annotationStorage.getValue(
        field.id,
        {}
      );
      // console.log(name, id, initialValue, override, field);
      const value = override?.value ?? initialValue;
      const element = document.getElementById(
        `acro-field-value-${field.id}`
      ) as HTMLInputElement | HTMLTextAreaElement;
      element.value =
        typeof value === 'boolean' &&
        ['checkbox', 'radiobutton'].includes(field.type)
          ? value
            ? 'On'
            : 'Off'
          : String(value);
      dispatchChange(element);
      if (field.id === eventFieldId) {
        scrollIntoViewIfNeeded(element);
        fixScrollTop('handleAnnotationChange - scrollIntoView');
      }
    }
  }

  syncFieldMappedStatus(
    annotationInput: HTMLElement,
    expressionInput: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  ) {
    const value = expressionInput.value.trim();
    annotationInput.classList.toggle('cumulus-mapped', value !== '');
    annotationInput.title = value;
  }

  async syncAcroField(event: Event) {
    if (!this.viewerApplication) {
      return;
    }
    if (event.target instanceof HTMLAnchorElement) {
      event.preventDefault();
    }
    const viewerApplication = this.viewerApplication;
    const target = event.target as HTMLElement;
    const id = target.getAttribute('element-id')!;
    const field = this.fieldsById[id];

    // render page if not already rendered
    const page = viewerApplication.pdfViewer.getPageView(field.page!);
    viewerApplication.appConfig.toolbar.pageNumber.value = String(
      field.page! + 1
    );
    viewerApplication.appConfig.toolbar.pageNumber.dispatchEvent(
      new Event('change', { bubbles: true })
    );
    // viewerApplication.eventBus.dispatch('pagenumberchanged', {
    //   source: self,
    //   value: field.page! + 1
    // });
    if (page.renderingState !== 3 /* PDFRenderingStates.FINISHED */) {
      await Promise.all([
        new Promise<void>(resolve =>
          viewerApplication.eventBus.on('pagerendered', resolve, {
            once: true,
            signal: this.detachedController?.signal
          })
        ),
        new Promise<void>(resolve =>
          viewerApplication.eventBus.on('annotationlayerrendered', resolve, {
            once: true,
            signal: this.detachedController?.signal
          })
        )
      ]);
    }

    const element = viewerApplication.appConfig.viewerContainer.querySelector(
      `[data-element-id="${id}"]`
    ) as HTMLElement;

    if (event.type === 'change') {
      this.syncFieldMappedStatus(
        element,
        target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      );
    } else {
      // click (on anchor) or expression focus/blur

      // focus (which will scroll field into view) OR only scroll into view
      if (target instanceof HTMLAnchorElement) {
        element.focus();
        fixScrollTop('syncAcroField - focus');
      } else {
        scrollIntoViewIfNeeded(element);
        fixScrollTop('syncAcroField - scrollIntoView');
      }
      // remove previous outline
      clearTimeout(this.viewerOutlineTimeout);
      this.removeViewerOutline();
      this.removeViewerOutline = () => {};
      // outline current field
      if (event.type !== 'blur') {
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        element.classList.add('active-field-outline');
        this.removeViewerOutline = () =>
          element.classList.remove(
            'active-field-outline',
            'active-field-outline--fade'
          );
      }
      // enqueue outline removal
      if (target instanceof HTMLAnchorElement) {
        element.classList.add('active-field-outline--fade');
        this.viewerOutlineTimeout = setTimeout(this.removeViewerOutline, 2050);
      }
    }
  }

  onAnnotationLayerRendered(viewerApplication: PDFViewerApplication): void {
    for (const { id } of Object.values(this.acroForm)
      .flat()
      .filter(x => ['text', 'checkbox', 'radiobutton'].includes(x.type))) {
      const annotationInput =
        viewerApplication.appConfig.viewerContainer.querySelector(
          `[data-element-id="${id}"]`
        ) as HTMLElement;
      if (!annotationInput) {
        continue;
      }
      const expressionInput = this.form.querySelector(
        `textarea[element-id="${id}"]`
      );
      if (!expressionInput) {
        continue;
      }
      this.syncFieldMappedStatus(
        annotationInput,
        expressionInput as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement
      );
    }
  }

  renderMappingFields(viewerApplication: PDFViewerApplication) {
    const dataFields: PdfFieldMapping[] = JSON.parse(
      this.fieldsScript.textContent!
    );
    let templates: unknown[] = [];
    for (const name of getNamesInPositionOrder(this.acroForm)) {
      const fields = this.acroForm[name].filter(field =>
        ['text', 'checkbox', 'radiobutton'].includes(field.type)
      );
      for (const field of fields) {
        const index = templates.length;
        const dataField = dataFields.find(
          dataField =>
            dataField.name === field.name &&
            dataField.export_values === field.exportValues
        );
        if (dataField && dataField.value !== field.value) {
          viewerApplication.pdfDocument.annotationStorage.setValue(field.id, {
            value: dataField.value
          });
        }
        templates.push(
          html`<section
            id="acro-field-${field.id}"
            class="rounded-1 p-3 mt-n3 ml-n3 mb-n3 mr-n3"
          >
            <h3 class="f4 mb-2">
              ${field.editable === false || field.hidden === true
                ? html`${breakAcroFieldName(field.name)}
                  ${field.type === 'radiobutton' && field.exportValues
                    ? `/ ${field.exportValues}`
                    : null}`
                : html`<a
                    class="Link--primary"
                    href="#"
                    tabindex="-1"
                    element-id="${field.id}"
                    data-action="click:pdf-template#syncAcroField"
                    >${breakAcroFieldName(field.name)}${field.type ===
                      'radiobutton' && field.exportValues
                      ? ` / ${field.exportValues}`
                      : null}</a
                  >`}
              <span class="Label Label--primary text-capitalize"
                >${field.type}</span
              >
              ${field.type === 'text' && field.charLimit
                ? html`<span class="Label">${field.charLimit} characters</span>`
                : null}
              ${field.type === 'text' && field.multiline
                ? html`<span class="Label">Multiline</span>`
                : null}
              ${field.editable === false
                ? html`<span class="Label Label--attention">Read only</span>`
                : null}
              ${field.hidden === true
                ? html`<span class="Label Label--attention">Hidden</span>`
                : null}
            </h3>
            ${formGroupTemplate({
              type: 'textarea',
              label: 'Mapping',
              value: dataField?.expression
                ? printExpression(dataField.expression.ast)
                : '',
              maxLength: 3000,
              disabled: this.titleInput.disabled,
              spellcheck: false,
              rows: 2,
              name: `mapping/fields/${index}/expression:nullstring`,
              inputAttrs: [attr('element-id', field.id)],
              action:
                'focus:pdf-template#syncAcroField blur:pdf-template#syncAcroField change:pdf-template#syncAcroField',
              classes: 'position-relative',
              headerClasses: 'sr-only'
            })}
            <details class="details-reset">
              <summary class="note" tabindex="-1">Details</summary>
              <div class="d-flex flex-column gap-3 mt-2 ml-3">
                ${formGroupTemplate({
                  type: field.multiline === true ? 'textarea' : 'text',
                  label: 'Value',
                  value: dataField ? dataField.value : field.value,
                  maxLength:
                    field.type === 'text' && field.charLimit
                      ? field.charLimit
                      : 300,
                  name: `mapping/fields/${index}/value`,
                  disabled: true,
                  id: `acro-field-value-${field.id}`
                })}
                <pre><code class="p-3 f6 overflow-auto">${JSON.stringify(
                  field,
                  null,
                  2
                )}</code></pre>
              </div>
            </details>
            <input
              type="hidden"
              name="mapping/fields/${index}/name"
              value="${field.name}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/type"
              value="${field.type}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/id"
              value="${field.id}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/page:integer"
              value="${field.page}"
            />
            ${field.editable === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/editable:boolean"
                  value="${field.editable}"
                />`}
            ${field.hidden === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/hidden:boolean"
                  value="${field.hidden}"
                />`}
            ${field.multiline === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/multiline:boolean"
                  value="${field.multiline}"
                />`}
            ${field.charLimit === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/charlimit:integer"
                  value="${field.charLimit}"
                />`}
            ${field.exportValues === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/export_values"
                  value="${field.exportValues}"
                />`}
            ${field.defaultValue === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/default_value"
                  value="${field.defaultValue}"
                />`}
          </section>`
        );
      }
    }
    this.form.insertAdjacentHTML('beforeend', render(templates));
    (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
  }
}

function getNamesInPositionOrder(acroForm: FieldMap): string[] {
  return Object.values(acroForm)
    .flat()
    .filter(widget => ['text', 'checkbox', 'radiobutton'].includes(widget.type))
    .map(widget => ({
      page: widget.page ?? 1000, // fallback if page is missing
      name: widget.name,
      rect: widget.rect ?? [0, 0] // fallback if rect is missing
    }))
    .sort((a, b) => {
      // sort by page ascending, then Y descending (top to bottom), then X ascending (left to right)
      const dp = a.page - b.page;
      const dy = b.rect[1] - a.rect[1];
      return dp !== 0 ? dp : dy !== 0 ? dy : a.rect[0] - b.rect[0];
    })
    .map(widget => widget.name)
    .filter((x, i, a) => a.indexOf(x) === i); // remove duplicates
}

function breakAcroFieldName(name: string) {
  return name
    .split(/\]\./g)
    .map((x, i) => (i === 0 ? x : [html`]<wbr />.${x}`]))
    .flat();
}

function fixScrollTop(message: string) {
  if (document.documentElement.scrollTop > 0) {
    document.documentElement.scrollTop = 0;
    console.log('fixScrollTop', message);
  }
  setTimeout(fixScrollTop, 0, message + ' + timeout');
}

/*
const serializedSignatures: any[] = [];
const annotationStorage =
  this.viewerApplication!.pdfDocument.annotationStorage;
const serializable = annotationStorage.serializable.map!;
for (const [key, editor] of annotationStorage) {
  console.log(key, editor, serializable.get(key));
  if (editor.name !== 'signatureEditor') {
    continue;
  }
  // serializedSignatures.push(serializable.get(key)); // seems to serialize in the wrong format.
  serializedSignatures.push(editor.serialize(true));
}
localStorage.setItem('signatures', JSON.stringify(serializedSignatures));




await new Promise<void>(resolve => setTimeout(resolve, 1000)); // need to wait for some reason, otherwise getLayer returns null!
const existing = localStorage.getItem('signatures');
if (existing) {
  // localStorage.removeItem('signatures');
  for (const editorData of JSON.parse(existing)) {
    const layer = this.annotationEditorUiManager!.getLayer(
      editorData.pageIndex
    );
    delete editorData.isCopy;
    const editor = await layer.deserialize(editorData);
    layer.add(editor);
  }
}

const {
  SignatureExtractor,
  AnnotationEditorType,
  AnnotationEditorParamsType
} = this.iframe.contentWindow.pdfjsLib;

const data = SignatureExtractor.extractContoursFromText(
  'Signature Placeholder', // text,
  {
    fontFamily:
      '"Brush script", "Apple Chancery", "Segoe script", "Freestyle Script", "Palace Script MT", "Brush Script MT", TK, cursive, serif',
    fontStyle: 'italic',
    fontWeight: '400'
  }, // fontInfo,
  0, // pageWidth,
  0, // pageHeight,
  0, // rotation,
  3 // SignatureEditor._INNER_MARGIN
);

const {
  newCurves, // array of Float32Array points
  areContours, // true for typed text
  mustSmooth,
  thickness,
  width,
  height
} = data;

await this.annotationEditorUiManager.updateMode(
  AnnotationEditorType.SIGNATURE
);
viewerApplication.eventBus.dispatch('switchannotationeditorparams', {
  source: this,
  type: AnnotationEditorParamsType.CREATE,
  value: {
    signatureData: {
      lines: {
        curves: newCurves.map(points => ({ points })),
        thickness,
        width,
        height
      },
      mustSmooth,
      areContours,
      description: 'hello world',
      uuid: crypto.randomUUID(),
      heightInPage: 40 //DEFAULT_HEIGHT_IN_PAGE,
    }
  }
});


_onResized
_onTranslated


- [ ] KBA / PIN
- [ ] UI for adding signers and sequencing them.
   Signer:
   - Role: (e.g "Employer", "Applicant", "Authorized Representative")
   - Identity:
      - Provide name and email when sending
      - Person associated with the activity
      - Person associated with the parent activity
   - Message
   - Placeholders (json)
- [ ] DB update
- [ ] List signature placeholders for each signer.
- [ ] Ability to add signature placeholder for signer.
- [ ] Handle mousein/mouseout placeholder 
- [ ] If role changes, update all placeholders for that signer.
- [ ] Handle move/resize
- [ ] Validate signer has at least one placeholder.
- [ ] Restore placeholders on load.
- [ ] Hide button to edit description

*/
